import React, { useState, useEffect } from 'react';
import WorkbookRouter from './WorkbookRouter';
import { db } from '../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const QUESTIONS = [
  { section: 1, sectionTitle: 'How often are you actually using AI?', q: 'How often do you currently use any AI tool for work tasks?', sub: 'Think about the last 30 days.', options: ['Never. I have not used AI for work.', 'Once or twice total, just experimenting.', 'A few times a month, but not consistently.', 'Several times a week for specific tasks.', 'Daily. It is part of how I work.'] },
  { section: 1, sectionTitle: 'How often are you actually using AI?', q: 'When you use AI at work, what are you primarily using it for?', sub: 'Pick the one that describes your most common use.', options: ['I have not found a real use case yet.', 'Writing or editing emails and documents.', 'Summarizing or researching information.', 'Multiple use cases across different tasks.', 'Building workflows, automations, or helping others use it too.'] },
  { section: 2, sectionTitle: 'How confident are you, really?', q: 'When you sit down to use an AI tool for a new task, how do you feel?', sub: 'Be honest. There is no wrong answer.', options: ['Anxious. I am not sure what I am doing and I worry I will mess something up.', 'Uncertain. I give it a try but I do not have much confidence in the result.', 'Cautiously optimistic. I figure it out as I go.', 'Comfortable. I know what I am doing most of the time.', 'Completely at ease. I know how to get what I need.'] },
  { section: 2, sectionTitle: 'How confident are you, really?', q: 'How would you rate the quality of the results you get from AI tools right now?', sub: 'Think about actual outputs you have used, not ideal scenarios.', options: ['Poor. The results are usually not usable.', 'Inconsistent. Sometimes good, often not.', 'Decent. I usually get something I can work with after editing.', 'Good. The outputs are solid and save me real time.', 'Excellent. I consistently get high-quality results.'] },
  { section: 3, sectionTitle: 'How broadly are you applying AI?', q: 'Across all the work you do in a typical week, how many distinct tasks do you currently use AI for?', sub: 'Think across writing, research, analysis, communication, planning, and beyond.', options: ['None. I have not found a real use case yet.', 'One or two. I have a narrow set of things I use it for.', 'Three to five. I use it across a handful of different tasks.', 'Six or more. I am applying it broadly across my role.', 'It touches almost everything I do. I default to AI as a first step.'] },
  { section: 3, sectionTitle: 'How broadly are you applying AI?', q: 'Has AI changed how you approach any part of your job, or is it just a faster way to do the same things?', sub: 'This is about workflow change, not just speed.', options: ['It has not changed anything. I do the same work the same way.', 'Mostly just faster. Same tasks, less time.', 'A little of both. Some things are genuinely different, most are just faster.', 'It has meaningfully changed how I approach several parts of my role.', 'It has fundamentally shifted how I think about my work.'] },
  { section: 4, sectionTitle: 'Where do you stand on AI?', q: 'How likely are you to recommend AI tools to a colleague on your team?', sub: '1 = Would not recommend at all, 5 = Would recommend enthusiastically.', options: ['Would not recommend. I am not convinced enough to put my name on it.', 'Probably not. I am still figuring it out myself.', 'Maybe. Depends on the person and the task.', 'Probably yes. I have already mentioned it to a few people.', 'Enthusiastically and often. I am already the person people ask.'] },
  { section: 4, sectionTitle: 'Where do you stand on AI?', q: 'Which statement honestly describes how you feel about AI and your job right now?', sub: 'Pick the one that is most true, even if it is uncomfortable.', options: ['Honestly? I am worried AI could replace parts of what I do.', 'Skeptical. I do not think it will make much difference for my specific role.', 'Curious but not there yet. I have not figured out the right use cases.', 'Intentional. I see the potential and I am starting to use it more strategically.', 'All in. I think this changes everything and I want to lead it.'] },
];

function AssessmentPreview({ type }) {
  const sections = [1, 2, 3, 4];
  const sectionLabels = { 1: 'Frequency', 2: 'Confidence', 3: 'Use Case Range', 4: 'Advocacy' };
  return (
    <div style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none' }}>
      <div style={{ background: '#1B2A4A', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '4px' }}>{type === 'pre' ? 'Pre-Assessment' : 'Post-Assessment'}</div>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#fff' }}>AI Readiness Snapshot</div>
        </div>
        <div style={{ fontSize: '11px', fontWeight: '600', padding: '4px 12px', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>Preview — Read Only</div>
      </div>
      <div style={{ padding: '24px 28px' }}>
        <div style={{ display: 'flex', marginBottom: '28px', borderBottom: '2px solid #E8E6E2' }}>
          {sections.map(s => (
            <div key={s} style={{ flex: 1, padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: s === 1 ? '#1B2A4A' : '#8A8A8A', borderBottom: s === 1 ? '2px solid #1B2A4A' : 'none', marginBottom: '-2px' }}>
              {sectionLabels[s]}
            </div>
          ))}
        </div>
        {QUESTIONS.map((q, i) => (
          <div key={i} style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: i < QUESTIONS.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#C0392B', marginBottom: '6px' }}>
              Section {q.section} — {sectionLabels[q.section]}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#3D3D3D', marginBottom: '4px', lineHeight: '1.5' }}>{q.q}</div>
            {q.sub && <div style={{ fontSize: '12px', color: '#8A8A8A', marginBottom: '12px', fontWeight: '300' }}>{q.sub}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {q.options.map((opt, vi) => (
                <div key={vi} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', background: '#FAF8F5', border: '1.5px solid #E8E6E2', fontSize: '13px', lineHeight: '1.4', color: '#3D3D3D' }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1.5px solid #E8E6E2', background: '#fff', flexShrink: 0, marginTop: '1px' }} />
                  {opt}
                </div>
              ))}
            </div>
          </div>
        ))}
        {type === 'pre' && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#3D3D3D', marginBottom: '4px', lineHeight: '1.5' }}>Anything else you want your facilitator to know?</div>
            <div style={{ fontSize: '12px', color: '#8A8A8A', marginBottom: '12px', fontWeight: '300' }}>Optional. This goes directly to your facilitator and helps personalize your training experience. Other participants cannot see this.</div>
            <div style={{ width: '100%', border: '1.5px solid #E8E6E2', padding: '12px 14px', background: '#FAF8F5', minHeight: '90px', fontSize: '13px', color: '#8A8A8A', fontStyle: 'italic' }}>
              Participant response appears here.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const DAY_TITLES = {
  1: 'Foundation and Prompting Fundamentals',
  2: 'Advanced Prompting',
  3: 'Use Case Identification',
  4: 'Role-Specific Application',
  5: 'Workflow Redesign',
  6: 'Agent Basics',
  7: 'Agent Building',
  8: 'Deploy and Iterate',
  9: 'Vibe Coding Part 1',
  10: 'Vibe Coding Part 2 and Program Close',
};

const formatKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bAi\b/g, 'AI')
    .replace(/\bRice\b/g, 'RICE')
    .replace(/\bUc\b/g, 'UC')
    .replace(/\bLib\b/g, 'Library');
};

export default function AdminWorkbookViewer({ user }) {
  const [participants, setParticipants] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [selectedDay, setSelectedDay] = useState(1);
  const [activeTab, setActiveTab] = useState('preview');
  const [workbookData, setWorkbookData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  useEffect(() => {
    const load = async () => {
      const usersSnap = await getDocs(collection(db, 'users'));
      const list = [];
      usersSnap.forEach(d => {
        const u = d.data();
        if (['tcfadmin'].includes(u.adminRole)) return;
        list.push({
          id: d.id,
          name: u.displayName || u.email || 'Unknown',
          company: u.company || '',
        });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setParticipants(list);
      setLoadingParticipants(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedId) { setWorkbookData(null); setAssessmentData(null); return; }
    const load = async () => {
      setLoading(true);
      const [wbSnap, assessSnap] = await Promise.all([
        getDoc(doc(db, 'workbooks', selectedId)),
        getDoc(doc(db, 'assessments', selectedId)),
      ]);
      setWorkbookData(wbSnap.exists() ? wbSnap.data() : {});
      setAssessmentData(assessSnap.exists() ? assessSnap.data() : null);
      setLoading(false);
    };
    load();
  }, [selectedId]);

  const dayKey = `day${selectedDay}`;
  const dayEntry = workbookData?.progress?.[dayKey] || {};
  const dayFields = dayEntry.sections || dayEntry.fields || {};
  const isComplete = dayEntry.status === 'complete' || dayEntry.completed === true;
  const hasResponses = Object.values(dayFields).some(v => typeof v === 'string' && v.trim());

  const selectedParticipant = participants.find(p => p.id === selectedId);

  const completedDays = workbookData
    ? ALL_DAYS.filter(d => {
        const e = workbookData.progress?.[`day${d}`] || {};
        return e.status === 'complete' || e.completed === true;
      })
    : [];

  // All tabs always visible. Responses tab only makes sense with a participant selected.
  const tabs = [
    { id: 'preview', label: 'Workbook Preview' },
    { id: 'pre', label: 'Pre-Assessment' },
    { id: 'post', label: 'Post-Assessment' },
    ...(selectedId ? [{ id: 'responses', label: 'Participant Responses' }] : []),
  ];

  return (
    <div style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <div style={s.heading}>Workbooks</div>
      <div style={s.sub}>
        Preview any workbook as participants see it, or select a participant to view their responses and assessment data.
      </div>

      {/* DAY SELECTOR — always visible */}
      <div style={s.dayTabRow}>
        {ALL_DAYS.map(d => {
          const entry = workbookData?.progress?.[`day${d}`] || {};
          const done = entry.status === 'complete' || entry.completed === true;
          const hasData = Object.values(entry.sections || entry.fields || {}).some(v => typeof v === 'string' && v.trim());
          return (
            <button
              key={d}
              style={{
                ...s.dayTab,
                ...(selectedDay === d ? s.dayTabActive : {}),
                ...(selectedId && done ? s.dayTabDone : {}),
              }}
              onClick={() => setSelectedDay(d)}
            >
              Day {d}
              {selectedId && done && <span style={s.checkMark}>✓</span>}
              {selectedId && !done && hasData && <span style={s.dotMark}>•</span>}
            </button>
          );
        })}
      </div>

      {/* PARTICIPANT SELECTOR */}
      <div style={s.selectorBar}>
        <div style={s.selectorLeft}>
          <label style={s.selectorLabel}>Participant (optional)</label>
          {loadingParticipants ? (
            <div style={{ fontSize: '13px', color: '#3D3D3D' }}>Loading participants...</div>
          ) : (
            <select
              style={s.participantSelect}
              value={selectedId}
              onChange={e => { setSelectedId(e.target.value); if (e.target.value && activeTab === 'preview') setActiveTab('responses'); }}
            >
              <option value="">View blank workbook...</option>
              {participants.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.company ? ` — ${p.company}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedParticipant && workbookData && (
          <div style={s.participantMeta}>
            <span style={s.metaItem}>{selectedParticipant.name}</span>
            <span style={s.metaDot}>·</span>
            <span style={s.metaItem}>{completedDays.length} of {ALL_DAYS.length} days complete</span>
          </div>
        )}
      </div>

      {/* CONTENT TABS */}
      <div style={s.tabBar}>
        {tabs.map(t => (
          <button
            key={t.id}
            style={{ ...s.tabBtn, ...(activeTab === t.id ? s.tabBtnActive : {}) }}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: WORKBOOK PREVIEW — real workbook loaded with admin's own user */}
      {activeTab === 'preview' && (
        <div style={s.previewWrap}>
          <div style={s.previewBanner}>
            Previewing Day {selectedDay} — {DAY_TITLES[selectedDay]}. This is exactly what participants see. Select a participant above to view their responses.
          </div>
          <WorkbookRouter user={user} workbook={{ day: selectedDay }} onNav={() => {}} />
        </div>
      )}

      {/* TAB: PARTICIPANT RESPONSES */}
      {activeTab === 'responses' && selectedId && (
        <div style={s.workbookArea}>
          {loading ? (
            <div style={{ fontSize: '13px', color: '#3D3D3D', padding: '20px' }}>Loading...</div>
          ) : (
            <>
              <div style={s.dayHeader}>
                <div style={s.dayHeaderLeft}>
                  <div style={s.dayTag}>Day {selectedDay}</div>
                  <div style={s.dayTitle}>{DAY_TITLES[selectedDay]}</div>
                </div>
                <div style={{
                  ...s.statusBadge,
                  ...(isComplete ? s.statusComplete : hasResponses ? s.statusInProgress : s.statusEmpty)
                }}>
                  {isComplete ? 'Complete' : hasResponses ? 'In Progress' : 'Not Started'}
                </div>
              </div>
              {!hasResponses ? (
                <div style={s.emptyState}>No responses for Day {selectedDay} yet.</div>
              ) : (
                <div style={s.responsesWrap}>
                  {Object.entries(dayFields)
                    .filter(([k, v]) => typeof v === 'string' && v.trim())
                    .map(([key, val]) => (
                      <div key={key} style={s.responseCard}>
                        <div style={s.responseLabel}>{formatKey(key)}</div>
                        <div style={s.responseValue}>{val}</div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* TAB: PRE-ASSESSMENT */}
      {activeTab === 'pre' && !selectedId && <AssessmentPreview type="pre" />}
      {activeTab === 'pre' && selectedId && (
        <div style={s.workbookArea}>
          {loading ? (
            <div style={{ fontSize: '13px', color: '#3D3D3D', padding: '20px' }}>Loading...</div>
          ) : (
            <>
              <div style={s.dayHeader}>
                <div style={s.dayHeaderLeft}>
                  <div style={s.dayTag}>Pre-Assessment</div>
                  <div style={s.dayTitle}>AI Readiness Snapshot</div>
                </div>
                <div style={{ ...s.statusBadge, ...(assessmentData?.pre ? s.statusComplete : s.statusEmpty) }}>
                  {assessmentData?.pre ? 'Complete' : 'Not Taken'}
                </div>
              </div>
              <div style={s.responsesWrap}>
                {/* Scores — always show even if zeros */}
                <div style={s.responseCard}>
                  <div style={s.responseLabel}>Scores</div>
                  <div style={s.responseValue}>
                    {assessmentData?.pre?.scores ? (
                      <>
                        {[
                          { key: 'freq', label: 'Frequency' },
                          { key: 'conf', label: 'Confidence' },
                          { key: 'range', label: 'Use Case Range' },
                          { key: 'adv', label: 'Advocacy' },
                        ].map(d => (
                          <div key={d.key} style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', maxWidth: '300px' }}>
                            <span>{d.label}</span>
                            <strong>{assessmentData.pre.scores[d.key]} / 5</strong>
                          </div>
                        ))}
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #E8E6E2', display: 'flex', justifyContent: 'space-between', maxWidth: '300px' }}>
                          <span style={{ fontWeight: '600' }}>Total</span>
                          <strong>{assessmentData.pre.scores.total} / 20</strong>
                        </div>
                        {assessmentData.pre.level && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#8A8A8A' }}>Level: {assessmentData.pre.level}</div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#8A8A8A', fontStyle: 'italic' }}>No scores recorded.</span>
                    )}
                  </div>
                </div>

                {/* Freeform — always show, blank or not */}
                <div style={s.responseCard}>
                  <div style={s.responseLabel}>Anything else you want your facilitator to know?</div>
                  <div style={s.responseValue}>
                    {assessmentData?.pre?.openText
                      ? assessmentData.pre.openText
                      : <span style={{ color: '#8A8A8A', fontStyle: 'italic' }}>No response provided.</span>
                    }
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB: POST-ASSESSMENT */}
      {activeTab === 'post' && !selectedId && <AssessmentPreview type="post" />}
      {activeTab === 'post' && selectedId && (
        <div style={s.workbookArea}>
          {loading ? (
            <div style={{ fontSize: '13px', color: '#3D3D3D', padding: '20px' }}>Loading...</div>
          ) : (
            <>
              <div style={s.dayHeader}>
                <div style={s.dayHeaderLeft}>
                  <div style={s.dayTag}>Post-Assessment</div>
                  <div style={s.dayTitle}>AI Readiness Snapshot — Post Training</div>
                </div>
                <div style={{ ...s.statusBadge, ...(assessmentData?.post ? s.statusComplete : s.statusEmpty) }}>
                  {assessmentData?.post ? 'Complete' : 'Not Taken'}
                </div>
              </div>
              <div style={s.responsesWrap}>
                <div style={s.responseCard}>
                  <div style={s.responseLabel}>Scores</div>
                  <div style={s.responseValue}>
                    {assessmentData?.post?.scores ? (
                      <>
                        {[
                          { key: 'freq', label: 'Frequency' },
                          { key: 'conf', label: 'Confidence' },
                          { key: 'range', label: 'Use Case Range' },
                          { key: 'adv', label: 'Advocacy' },
                        ].map(d => (
                          <div key={d.key} style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', maxWidth: '300px' }}>
                            <span>{d.label}</span>
                            <strong>{assessmentData.post.scores[d.key]} / 5</strong>
                          </div>
                        ))}
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #E8E6E2', display: 'flex', justifyContent: 'space-between', maxWidth: '300px' }}>
                          <span style={{ fontWeight: '600' }}>Total</span>
                          <strong>{assessmentData.post.scores.total} / 20</strong>
                        </div>
                        {assessmentData.post.level && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#8A8A8A' }}>Level: {assessmentData.post.level}</div>
                        )}
                        {assessmentData.pre?.scores && (
                          <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #E8E6E2', fontSize: '12px', color: '#1a8a4a', fontWeight: '600' }}>
                            Score change from pre: +{assessmentData.post.scores.total - assessmentData.pre.scores.total} points
                          </div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#8A8A8A', fontStyle: 'italic' }}>Post-assessment not yet completed.</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', marginBottom: '24px' },
  selectorBar: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
  selectorLeft: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, maxWidth: '440px' },
  selectorLabel: { fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' },
  participantSelect: { border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none', width: '100%' },
  participantMeta: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#3D3D3D' },
  metaItem: { fontWeight: '500', color: '#1B2A4A' },
  metaDot: { color: '#C9A84C' },
  dayTabRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' },
  dayTab: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: '500', color: '#3D3D3D', display: 'flex', alignItems: 'center', gap: '5px' },
  dayTabActive: { background: '#1B2A4A', color: '#D4A847', borderColor: '#1B2A4A', fontWeight: '600' },
  dayTabDone: { borderColor: '#3b6d11' },
  checkMark: { fontSize: '10px', color: '#3b6d11' },
  dotMark: { fontSize: '14px', color: '#D4A847', lineHeight: 1 },
  tabBar: { display: 'flex', gap: '0', borderBottom: '1.5px solid #E8E6E2', marginBottom: '0' },
  tabBtn: { background: '#F2F0EC', border: '1.5px solid #E8E6E2', borderBottom: 'none', padding: '10px 20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', color: '#8A8A8A', marginRight: '4px' },
  tabBtnActive: { background: '#1B2A4A', color: '#D4A847', borderColor: '#1B2A4A' },
  previewWrap: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none' },
  previewBanner: { background: '#fef5e4', borderBottom: '1.5px solid #f5d78a', padding: '10px 20px', fontSize: '12px', color: '#854f0b', fontWeight: '500' },
  workbookArea: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none' },
  dayHeader: { background: '#1B2A4A', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dayHeaderLeft: {},
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '4px' },
  dayTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#fff' },
  statusBadge: { fontSize: '11px', fontWeight: '600', padding: '4px 12px', letterSpacing: '0.5px' },
  statusComplete: { background: '#eaf3de', color: '#3b6d11' },
  statusInProgress: { background: '#fef5e4', color: '#854f0b' },
  statusEmpty: { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' },
  responsesWrap: { padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '12px' },
  responseCard: { border: '1.5px solid #E8E6E2' },
  responseLabel: { background: '#F2F0EC', padding: '8px 14px', fontSize: '11px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #E8E6E2' },
  responseValue: { padding: '12px 14px', fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', whiteSpace: 'pre-wrap' },
  emptyState: { padding: '28px', fontSize: '13px', color: '#3D3D3D', fontStyle: 'italic' },
};
